
import { Badge } from "../../../Components/Badge";
import { Button } from "../../../Components/Button";
import { Card, CardHeader, CardTitle, CardContent } from "../../../Components/Card";
import { AdminIcon } from "../../../Icons/AdminIcon";
import { Icon } from "../../../Icons/Icon";
import { CourseStatus } from "../../../Types/CourseTypes";
import { sanitizeChapterHtml } from "../../../Utils/sanitizeHtml";
import './CourseProductCard.css'
interface CourseProductCardProps {
  title: string;
  description: string;
  totalDuration: string;
  chaptersCount: number;
  enrolledUsers?: number;
  status: CourseStatus;
  variant?: 'compact' | 'expanded';
  price?: string;
  rating?: number;
  instructor?: string;
  thumbnailImage?: string;
  bannerImage?: string;
}

const CourseProductCard: React.FC<CourseProductCardProps> = ({
  title,
  description,
  totalDuration,
  chaptersCount,
  enrolledUsers = 0,
  status,
  variant = 'compact',
  price = '999 kr',
  rating = 4.8,
  instructor = 'Expertinstruktör',
  thumbnailImage,
  bannerImage
}) => {
  const descriptionHtml = sanitizeChapterHtml(description);
  const getStatusLabel = () => {
    switch (status) {
      case CourseStatus.Published:
        return 'Publicerad';
      case CourseStatus.Archived:
        return 'Arkiverad';
      default:
        return 'Utkast';
    }
  };

  const getStatusVariant = () => {
    switch (status) {
      case CourseStatus.Published:
        return 'default';
      case CourseStatus.Archived:
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  if (variant === 'compact') {
    return (
    
    <Card className="cpc-card cpc-card--compact" disableHover>
  <div className="cpc-thumb-wrapper">
    {thumbnailImage ? (
      <img src={thumbnailImage} alt={title} className="cpc-thumb-img" />
    ) : (
      <div className="cpc-thumb-placeholder">
        <AdminIcon.OpenBook />
      </div>
    )}
  </div>

    <CardHeader className="cpc-header-compact">
    <div className="cpc-header-row">
      <Badge variant={getStatusVariant()}>
        {getStatusLabel()}
      </Badge>

      <div className="cpc-rating">
        <Icon.Star />
        <span className="cpc-rating-number-sm">{rating}</span>
      </div>
    </div>

    <CardTitle className="cpc-title-compact">{title}</CardTitle>
  </CardHeader>

    <CardContent>
    <div
      className="cpc-description-compact"
      dangerouslySetInnerHTML={{ __html: descriptionHtml }}
    />

    <div className="cpc-meta-row">
      <div className="cpc-meta-left">
        <div className="cpc-meta-item">
          <AdminIcon.Clock />
          {totalDuration}
        </div>
        <div className="cpc-meta-item">
          <AdminIcon.Video />
          {chaptersCount}
        </div>
      </div>
    </div>

    <div className="cpc-price-row">
      <span className="cpc-price">{price}</span>
      <Button className="submit-button" text={'Köp kurs'} />
    </div>
  </CardContent>
</Card>
    );
  }

  
  return (
    
    <Card className="cpc-card cpc-card--expanded" disableHover>
  <div className="relative">
    <div className="cpc-banner-wrapper">
      {bannerImage ? (
        <img src={bannerImage} alt={title} className="cpc-banner-img" />
      ) : (
        <div className="cpc-banner-placeholder">
          <AdminIcon.OpenBook />
        </div>
      )}
    </div>

    <div className="absolute top-4 right-4">
      <Badge variant={getStatusVariant()} className="text-sm">
        {getStatusLabel()}
      </Badge>
    </div>
  </div>

  <CardHeader className="cpc-header-expanded">
    <div className="cpc-header-row">
      <div>
        <CardTitle className="cpc-title-expanded">{title}</CardTitle>
        <p className="text-sm text-muted-foreground">av {instructor}</p>
      </div>

      <div className="cpc-rating">
        <Icon.Star width={18}/>
        <span className="cpc-rating-number-lg">{rating}</span>
        <span className="text-sm text-muted-foreground ml-1">({enrolledUsers})</span>
      </div>
    </div>
  </CardHeader>

  <CardContent className="space-y-6">
    <div>
      <h3 className="font-semibold mb-2">Kursbeskrivning</h3>
      <div
        className="cpc-description-expanded"
        dangerouslySetInnerHTML={{ __html: descriptionHtml }}
      />
    </div>

    <div className="cpc-expanded-info-grid">
      <div className="cpc-info-block">
        <AdminIcon.Clock width={18}/>
        <span className="cpc-info-label">Total tid</span>
        <span className="cpc-info-value">{totalDuration}</span>
      </div>

      <div className="cpc-info-block">
        <AdminIcon.Video width={18}/>
        <span className="cpc-info-label">Kapitel</span>
        <span className="cpc-info-value">{chaptersCount} st</span>
      </div>

      <div className="cpc-info-block">
        <AdminIcon.UserGroup width={18}/>
        <span className="cpc-info-label">Deltagare</span>
        <span className="cpc-info-value">{enrolledUsers} st</span>
      </div>
    </div>

    <div className="cpc-price-box">
      <div>
        <p className="text-sm text-muted-foreground mb-1">Kurspris</p>
        <p className="text-4xl font-bold text-primary">{price}</p>
      </div>

      <Button className="submit-button" text={'Köp kurs nu'} />
    </div>

    <div className="cpc-feature-list">
      <h3 className="font-semibold mb-2">Vad du får:</h3>
      <ul className="space-y-2">
        <li className="cpc-feature-item">
          <span className="cpc-feature-check">✓</span>
          Livstidsåtkomst till allt material
        </li>
        <li className="cpc-feature-item">
          <span className="cpc-feature-check">✓</span>
          {chaptersCount} videolektioner med övningar
        </li>
        <li className="cpc-feature-item">
          <span className="cpc-feature-check">✓</span>
          Certifikat vid genomförande
        </li>
        <li className="cpc-feature-item">
          <span className="cpc-feature-check">✓</span>
          Tillgång till community
        </li>
      </ul>
    </div>
  </CardContent>
</Card>

  );
};

export default CourseProductCard;
